import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, CheckCircle2, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { pixelLead } from "@/lib/pixel";
import {
  BUSINESS_TYPES,
  quoteFormSchema,
  type QuoteFormValues,
} from "@/lib/quoteSchema";
import quoteService from "@/services/quoteService";
import { validateAndProcessImage } from "@/utils/imageValidator";
import { apiErrorMessage } from "@/lib/apiError";

/**
 * Formulario de cotización B2B (HU-02).
 *
 * Captura la solicitud estructurada inline en la home: datos de contacto,
 * detalle del pedido y logo opcional. Al enviar dispara el evento `Lead` del
 * funnel (RF-11) y confirma en pantalla (RF-1).
 */
const QuoteForm = () => {
  const { toast } = useToast();
  const [logo, setLogo] = useState<string | undefined>(undefined);
  const [logoName, setLogoName] = useState<string>("");
  const [logoError, setLogoError] = useState<string | undefined>(undefined);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<QuoteFormValues>({
    resolver: zodResolver(quoteFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      company: "",
      product: "",
      targetDate: "",
      message: "",
      website: "",
    },
  });

  /** Adjunta el logo validado (RF-12); si no sirve, avisa y deja enviar sin él (RF-13). */
  const handleLogoChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const result = await validateAndProcessImage(file);

    if (!result.valid) {
      setLogo(undefined);
      setLogoName("");
      setLogoError(
        result.errors[0]?.message ??
          "No pudimos usar ese archivo. Probá con otra imagen."
      );
      return;
    }

    setLogoError(undefined);
    setLogoName(file.name);
    setLogo(result.processedImage);
  };

  const onSubmit = async (values: QuoteFormValues) => {
    // Honeypot: si un bot lo completó, no se envía nada (el server también filtra).
    if (values.website) return;

    try {
      await quoteService.createQuote({
        name: values.name,
        email: values.email,
        phone: values.phone,
        businessType: values.businessType,
        quantity: values.quantity,
        company: values.company || undefined,
        product: values.product || undefined,
        targetDate: values.targetDate || undefined,
        message: values.message || undefined,
        image: logo,
      });

      pixelLead();

      toast({
        title: "¡Recibimos tu solicitud!",
        description:
          "Vivi te escribe en menos de 24 horas hábiles con tu propuesta.",
      });

      reset();
      setLogo(undefined);
      setLogoName("");
      setLogoError(undefined);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "No pudimos enviar tu solicitud",
        description:
          apiErrorMessage(error) ??
          "Revisá tu conexión e intentá de nuevo, o escribinos por WhatsApp.",
      });
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="bg-white rounded-3xl border border-primary/15 shadow-lg p-6 sm:p-8 text-left"
      noValidate
    >
      <div className="flex items-center gap-2 mb-6">
        <CheckCircle2 className="h-5 w-5 text-primary-dark" />
        <h3 className="font-playfair font-semibold text-xl text-brown-dark">
          Pedí tu cotización
        </h3>
      </div>

      {/* Honeypot: invisible para las personas */}
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
        {...register("website")}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label htmlFor="quote-name">Nombre y apellido *</Label>
          <Input id="quote-name" {...register("name")} />
          {errors.name && (
            <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="quote-email">Email *</Label>
          <Input id="quote-email" type="email" {...register("email")} />
          {errors.email && (
            <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="quote-phone">Teléfono *</Label>
          <Input
            id="quote-phone"
            inputMode="tel"
            placeholder="+507 6000-0000"
            {...register("phone")}
          />
          {errors.phone && (
            <p className="text-sm text-red-600 mt-1">{errors.phone.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="quote-business">Tipo de negocio *</Label>
          <Controller
            control={control}
            name="businessType"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger id="quote-business">
                  <SelectValue placeholder="Elegí una opción" />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPES.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.businessType && (
            <p className="text-sm text-red-600 mt-1">
              {errors.businessType.message}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="quote-quantity">Cantidad estimada *</Label>
          <Input
            id="quote-quantity"
            type="number"
            min={1}
            {...register("quantity")}
          />
          {errors.quantity && (
            <p className="text-sm text-red-600 mt-1">
              {errors.quantity.message}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="quote-company">Empresa o consultorio</Label>
          <Input id="quote-company" {...register("company")} />
          {errors.company && (
            <p className="text-sm text-red-600 mt-1">{errors.company.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="quote-product">Producto de interés</Label>
          <Input
            id="quote-product"
            placeholder="Planner, libreta de marca, agenda…"
            {...register("product")}
          />
          {errors.product && (
            <p className="text-sm text-red-600 mt-1">{errors.product.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="quote-date">Fecha objetivo</Label>
          <Input id="quote-date" type="date" {...register("targetDate")} />
        </div>

        <div>
          <Label htmlFor="quote-logo">Logo o referencia (opcional)</Label>
          <Input
            id="quote-logo"
            type="file"
            accept="image/*"
            className="cursor-pointer"
            onChange={handleLogoChange}
          />
          {logoName && !logoError && (
            <p className="text-xs text-green-700 mt-1 flex items-center gap-1">
              <Paperclip className="h-3 w-3" /> {logoName}
            </p>
          )}
          {logoError && (
            <p className="text-sm text-red-600 mt-1">
              {logoError} Podés enviar la solicitud sin logo y nos lo pasás
              después.
            </p>
          )}
        </div>

        <div className="sm:col-span-2">
          <Label htmlFor="quote-message">Contanos sobre tu pedido</Label>
          <Textarea
            id="quote-message"
            rows={4}
            placeholder="Para qué la querés, colores, si lleva el logo del consultorio…"
            {...register("message")}
          />
          {errors.message && (
            <p className="text-sm text-red-600 mt-1">{errors.message.message}</p>
          )}
        </div>
      </div>

      <Button
        type="submit"
        size="lg"
        disabled={isSubmitting}
        className="w-full sm:w-auto mt-6 bg-primary text-brown-dark hover:bg-primary-dark font-semibold px-10 py-4 rounded-full text-lg shadow-md transition-all duration-300 min-h-[52px]"
      >
        {isSubmitting ? "Enviando…" : "Pedir mi cotización"}
        {!isSubmitting && <ArrowRight className="h-5 w-5 ml-2" />}
      </Button>

      <p className="text-xs text-brown-light/80 mt-3">
        Usamos tus datos solo para responderte con la cotización.
      </p>
    </form>
  );
};

export default QuoteForm;
